import { Link } from "react-router-dom";
import { Spinner } from "../ui/index.jsx";
import { formatIDR } from "../../utils/format";
import { getStatusLabel } from "./AssetHelpers.jsx";
import { OWNERSHIP_CLASS_LABELS } from "../../utils/constants";
import { cn } from "../../utils/cn";
import { StackedBreakdown } from "../charts/StackedBreakdown.jsx";

/**
 * Asset Summary (CONTEXT.md): Finance's per-entity view, shown above the asset
 * list to financial roles.
 *
 * Cost and book value are measured at the END of the chosen month. Total assets
 * and both breakdowns are always CURRENT — the system keeps no history of
 * condition or ownership — and say so. Figures are set in ink; the one blue is
 * on the main data element of each breakdown, and red only on bad condition.
 */

const DATA_HUE = "bg-[#1d5fbf]";

// Condition: the bar segment colour and the row's dot. Active carries the one
// data hue, Damaged and Lost are the bad news, everything else is a grey ramp.
const CONDITION_ORDER = ["ACTIVE", "IDLE", "DAMAGED", "UNDER_REPAIR", "RETIRED", "LOST"];
const CONDITION_COLOR = {
  ACTIVE: DATA_HUE,
  IDLE: "bg-slate-400",
  DAMAGED: "bg-rose-500",
  UNDER_REPAIR: "bg-slate-600",
  RETIRED: "bg-slate-300",
  LOST: "bg-rose-300",
};

const OWNERSHIP_ORDER = ["PAID_OFF", "UNDER_INSTALLMENT", "RENTED_LEASED"];
const OWNERSHIP_COLOR = {
  PAID_OFF: DATA_HUE,
  UNDER_INSTALLMENT: "bg-slate-500",
  RENTED_LEASED: "bg-slate-300",
};

// The month-end is a UTC instant (31 Oct 23:59:59Z); show it in UTC so it never
// reads as the 1st of the next month on a machine ahead of UTC.
const formatUtcDay = (d) =>
  new Date(d).toLocaleDateString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric" });

function Figure({ label, value, caption, lead }) {
  return (
    <div className="px-5 py-4 min-w-0">
      <p className="text-sm text-slate-500">{label}</p>
      <p
        className={cn(
          "mt-1 font-semibold text-slate-900 tabular-nums truncate",
          lead ? "text-[32px] leading-10" : "text-2xl leading-9",
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-slate-400">{caption}</p>
    </div>
  );
}

function Breakdown({ title, caption, order, counts, colors, labelOf, total, selected, onSelect }) {
  const items = order.map((k) => ({ key: k, label: labelOf(k), value: counts?.[k] ?? 0, color: colors[k] }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-400">{caption}</span>
      </div>
      <StackedBreakdown items={items} total={total} selected={selected} onSelect={onSelect} />
    </div>
  );
}

export function AssetSummary({
  summary,
  isLoading,
  isError,
  scope, // entity + period only: what the "not valued" link keeps
  status,
  ownership,
  onSelectStatus,
  onSelectOwnership,
}) {
  if (isError) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 mb-4 text-sm text-rose-600">
        Could not load the asset summary. Please refresh.
      </div>
    );
  }
  if (isLoading || !summary) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-10 mb-4 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const { totals, valued, notValued, byCondition, byOwnership } = summary;
  const asOfLabel = `as of ${formatUtcDay(summary.asOf)}`;

  // The link shows the assets this summary could not value — in the same
  // entity — and nothing else, so no other list filter is carried over.
  const notValuedParams = new URLSearchParams(scope);
  notValuedParams.set("notValued", "true");

  return (
    <div className="space-y-4 mb-4">
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-y sm:divide-y-0 divide-slate-200">
          <Figure
            label="Acquisition cost"
            value={formatIDR(totals.cost)}
            caption={`${asOfLabel} · ${valued.cost.toLocaleString()} assets`}
          />
          <Figure
            lead
            label="Book value"
            value={formatIDR(totals.bookValue)}
            caption={`${asOfLabel} · ${valued.bookValue.toLocaleString()} assets`}
          />
          <Figure
            label="Total assets"
            value={totals.assets.toLocaleString()}
            caption="as of today"
          />
        </div>
        <div className="border-t border-slate-200 px-5 py-2.5 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
          <span>Includes retired, lost and rented / leased assets.</span>
          {notValued.count > 0 && (
            <Link
              to={`/assets?${notValuedParams.toString()}`}
              className="text-slate-500 hover:text-slate-800 underline underline-offset-2"
              title={[
                notValued.missingDate && `${notValued.missingDate} without acquisition date`,
                notValued.missingPrice && `${notValued.missingPrice} without acquisition price`,
                notValued.missingLife && `${notValued.missingLife} without useful life`,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {notValued.count.toLocaleString()} {notValued.count === 1 ? "asset" : "assets"} not valued — missing date, price or useful life
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Breakdown
          title="Condition"
          caption="as of today"
          order={CONDITION_ORDER}
          counts={byCondition}
          colors={CONDITION_COLOR}
          labelOf={getStatusLabel}
          total={totals.assets}
          selected={status}
          onSelect={onSelectStatus}
        />
        <Breakdown
          title="Ownership"
          caption="as of today"
          order={OWNERSHIP_ORDER}
          counts={byOwnership}
          colors={OWNERSHIP_COLOR}
          labelOf={(k) => OWNERSHIP_CLASS_LABELS[k]}
          total={totals.assets}
          selected={ownership}
          onSelect={onSelectOwnership}
        />
      </div>
    </div>
  );
}
